import cx from 'classnames';
import ReactModal from 'react-modal';
import { IconButton } from '../presentational/buttons/IconButton';
import { CSSProperties, ReactNode, DetailedHTMLProps, HTMLAttributes, useEffect } from 'react';

type ModalProps = {
  children?: ReactNode;
  className?: string;
  id?: string;
  isOpen?: boolean;
  onRequestClose?: () => void;
  overlayClassName?: string;
  overlayStyles?: CSSProperties;
  classModalContent?: string;
  shouldCloseOnOverlayClick?: boolean;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnReactModalContentClick?: boolean;
  style?: CSSProperties;
  showOverlayCloseButton?: boolean;
  title?: ReactNode;
  size?: 'default' | 'large';
  centered?: boolean;
  theme?: 'default' | 'gray';
};

const Modal = (props: ModalProps) => {
  const {
    id,
    isOpen,
    children,
    className,
    onRequestClose,
    overlayClassName,
    shouldCloseOnOverlayClick = true,
    shouldCloseOnEsc = true,
    shouldCloseOnReactModalContentClick = false,
    classModalContent,
    style,
    showOverlayCloseButton,
    title,
    size = 'default',
    centered = false,
    theme = 'default',
    overlayStyles,
    ...otherProps
  } = props;

  // Defensive cleanup for react-modal bug #888 exposed by React 19.
  // When a modal is unmounted before react-modal's internal setState takes effect,
  // the body class isn't removed. This cleanup ensures scroll is restored.
  // See: https://github.com/reactjs/react-modal/issues/888#issuecomment-1158061329
  useEffect(() => {
    return () => {
      // Only remove the body class if no other modal overlays exist
      const openModals = document.querySelectorAll('.ReactModal__Overlay');
      if (openModals.length === 0) {
        document.body.classList.remove('ReactModal__Body--open');
      }
    };
  }, []);

  return (
    <ReactModal
      className={cx('rio-modal', className, {
        'rio-modal--large': size === 'large',
        'rio-modal--centered': centered,
        'rio-modal--gray': theme === 'gray'
      })}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName={cx('rio-modal__overlay', overlayClassName)}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      style={style}
      ariaHideApp={false}
      contentElement={(contentElementProps: DetailedHTMLProps<any, any>, contentElement: ReactNode) => (
        <div {...contentElementProps} onClick={shouldCloseOnReactModalContentClick ? onRequestClose : undefined}>
          {contentElement}
        </div>
      )}
      overlayElement={
        showOverlayCloseButton || overlayStyles
          ? (overlayElementProps: DetailedHTMLProps<any, any>, contentElement: ReactNode) => (
              <div {...overlayElementProps} style={overlayStyles}>
                {showOverlayCloseButton && (
                  <IconButton
                    variant="text"
                    className="rio-modal__close"
                    onClick={onRequestClose}
                    icon={<i className="fa fa-times" />}
                  />
                )}
                {contentElement}
              </div>
            )
          : undefined
      }
      {...otherProps}
    >
      <div className={cx('rio-modal__content', classModalContent)} id={id || ''} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="raider-modal__header">
            <h2 className="rio-modal__header-title">{title}</h2>
            <IconButton variant="text" onClick={onRequestClose} icon={<i className="fa fa-times fa-xl" />} />
          </div>
        )}
        {children}
      </div>
    </ReactModal>
  );
};

export const ModalBody = ({
  children,
  className,
  ...restProps
}: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => {
  return (
    <div className={cx('rio-modal__body slds-text-body--regular', className)} {...restProps}>
      {children}
    </div>
  );
};

export const ModalFooter = ({
  children,
  className,
  ...restProps
}: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => {
  return (
    <div className={cx('rio-modal__footer', className)} {...restProps}>
      {children}
    </div>
  );
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
